<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { validateCodeSecurity } from "@/util/securityValidation";
import { UIModal, UIModalContent, UIModalFooter, UIModalHeader } from "@gohighlevel/ghl-ui";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import CodeEditor from "./CodeEditor.vue";

const { t } = useI18n();

const props = defineProps({
  modelActive: {
    type: Boolean,
    default: false,
  },
  code: {
    type: String,
    default: "",
  },
});
const showModalNative = ref(props.modelActive);
const emit = defineEmits(["update:showModel", "update:submit", "update:cancel", "update:close"]);
const store = useAppStore();

watch(
  () => showModalNative.value,
  (newValue) => {
    emit("update:showModel", newValue);
  }
);
const inputValue = ref(props.code || "");
const cmOption = reactive({
  tabSize: 4,
  styleActiveLine: true,
  lineNumbers: true,
  line: true,
  mode: "text/html",
  lineWrapping: true,
  theme: "dracula",
});

/**
 * @description Computed security violations with translated messages
 */
const securityViolations = computed(() => {
  const violations = validateCodeSecurity(inputValue.value);
  return violations.map((v) => ({
    message: t(v.messageKey),
    severity: v.severity,
  }));
});

const hasSecurityErrors = computed(() =>
  securityViolations.value.some((v) => v.severity === "error")
);

const closeEditor = () => {
  showModalNative.value = false;
  emit("update:close", inputValue.value);
};

const submitCallback = () => {
  // Block submission if there are security errors
  if (hasSecurityErrors.value) {
    return;
  }

  showModalNative.value = false;
  setTimeout(() => {
    store.addToHistory();
  }, 100);
  emit("update:submit", inputValue.value);
};

const cancelCallback = () => {
  showModalNative.value = false;
  emit("update:cancel", inputValue.value);
};

const codeChangeListner = (updatedCode: string) => {
  inputValue.value = updatedCode;
};
</script>

<template>
  <div>
    <UIModal id="model" v-model:show="showModalNative" :width="700">
      <template #header>
        <UIModalHeader id="model-header" :title="t('common.htmlEditorHeader')" @close="closeEditor">
        </UIModalHeader>
      </template>
      <UIModalContent class="py-3 text-center">
        <CodeEditor
          ref="myCm"
          :code-input="inputValue"
          :placeholder="t('common.htmlEditorBodyMessage')"
          :height="400"
          :options="cmOption"
          @update:input-code="codeChangeListner"
        />
        <!-- Security Validation Alert -->
        <div
          v-if="securityViolations.length > 0"
          class="mt-3 rounded-md border border-red-200 bg-red-50 p-4 text-left"
        >
          <p class="mb-2 text-sm font-medium text-red-700">
            {{ t("common.securityValidation.title") }}
          </p>
          <ul class="list-disc space-y-1 pl-5 text-sm text-red-700">
            <li v-for="(violation, index) in securityViolations" :key="index">
              {{ violation.message }}
            </li>
          </ul>
        </div>
      </UIModalContent>
      <template #footer>
        <UIModalFooter
          id="modal-footer"
          :positive-text="t('common.yesSave')"
          :negative-text="t('common.cancelKeep')"
          :disabled="hasSecurityErrors"
          @positive-click="submitCallback"
          @negative-click="cancelCallback"
        >
        </UIModalFooter>
      </template>
    </UIModal>
  </div>
</template>
